"use client";

import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

import ConfirmationModal from "@/components/atoms/modal/confirmation-modal";
import {
  PAYMENT_ALREADY_CAPTURED_PARAM,
  PAYMENT_ALREADY_CAPTURED_VALUE,
} from "@/constants";

/**
 * PaymentAlreadyCapturedModal
 *
 * A reusable modal component that displays when payment has already been captured.
 * Used in both quote and renewal flows to inform users that their payment is complete
 * and the policy is active.
 *
 * Triggers when URL contains `?paymentAlreadyCaptured=true` query parameter.
 * Automatically closes after 5 seconds if user doesn't click OK.
 */
function PaymentAlreadyCapturedModal() {
  const searchParams = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const paymentAlreadyCaptured = searchParams?.get(
      PAYMENT_ALREADY_CAPTURED_PARAM
    );
    if (paymentAlreadyCaptured === PAYMENT_ALREADY_CAPTURED_VALUE) {
      setIsModalOpen(true);
    }
  }, [searchParams]);

  // Auto-close modal after 5 seconds
  useEffect(() => {
    if (isModalOpen) {
      const timer = setTimeout(() => {
        handleConfirm();
      }, 5000);

      // Cleanup timer on unmount or when modal closes
      return () => clearTimeout(timer);
    }
  }, [isModalOpen]);

  const handleConfirm = () => {
    setIsModalOpen(false);
    // Remove the query parameter from URL
    const url = new URL(window.location.href);
    url.searchParams.delete(PAYMENT_ALREADY_CAPTURED_PARAM);
    window.history.replaceState({}, "", url.toString());
  };

  return (
    <ConfirmationModal
      isOpen={isModalOpen}
      modalTitleText="You're all set"
      modalSubText="Your payment has already been completed, and your policy is now active."
      modalConfirmBtnText="OK"
      modalConfirmBtnClick={handleConfirm}
    />
  );
}

export default PaymentAlreadyCapturedModal;
